import React, { useState } from 'react';
import { updateProject, deleteProject } from './api';

const COLOR_DEFAULTS = {
  primaryColor: '#E85D04',
  backgroundColor: '#F7F5F2',
  buttonTextColor: '#FFFFFF',
};

const LANGUAGE_PRESETS = {
  en: {
    label: 'English',
    landingTitle: 'Ready to start?',
    landingSubtitle:
      'Tap below to allow microphone access and begin your voice survey. Keep this screen open while you talk.',
    landingButton: 'Allow microphone access and start',
    landingFooter:
      'Works best in Chrome or Safari. Do not lock your phone during the survey.',
  },
  es: {
    label: 'Español (LATAM)',
    landingTitle: '¿Listo para comenzar?',
    landingSubtitle:
      'Toca el botón de abajo para permitir el acceso al micrófono y comenzar tu encuesta de voz. Mantén esta pantalla abierta mientras hablas.',
    landingButton: 'Permitir acceso al micrófono y comenzar',
    landingFooter:
      'Funciona mejor en Chrome o Safari. No bloquees el teléfono durante la encuesta.',
  },
  hi: {
    label: 'हिन्दी',
    landingTitle: 'शुरू करने के लिए तैयार हैं?',
    landingSubtitle:
      'माइक्रोफ़ोन की अनुमति देने और अपनी वॉइस सर्वे शुरू करने के लिए नीचे बटन दबाएँ। बात करते समय यह स्क्रीन खुली रखें।',
    landingButton: 'माइक्रोफ़ोन की अनुमति दें और शुरू करें',
    landingFooter:
      'Chrome या Safari में सबसे अच्छा काम करता है। सर्वे के दौरान फ़ोन लॉक न करें।',
  },
};

const LANDING_DEFAULTS = LANGUAGE_PRESETS.en;

function landingFieldValue(saved, fallback) {
  return saved != null ? saved : fallback;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read image'));
    reader.readAsDataURL(file);
  });
}

function ProjectSettings({ theme, project, onUpdated, onDeleted }) {
  const [name, setName] = useState(project.name || '');
  const [agentId, setAgentId] = useState(project.agentId || '');
  const [defaultTag, setDefaultTag] = useState(project.defaultTag || 'whatsapp');
  const [requireMicGate, setRequireMicGate] = useState(project.requireMicGate !== false);
  const [primaryColor, setPrimaryColor] = useState(project.primaryColor || COLOR_DEFAULTS.primaryColor);
  const [backgroundColor, setBackgroundColor] = useState(
    project.backgroundColor || COLOR_DEFAULTS.backgroundColor
  );
  const [buttonTextColor, setButtonTextColor] = useState(
    project.buttonTextColor || COLOR_DEFAULTS.buttonTextColor
  );
  const [landingTitle, setLandingTitle] = useState(
    landingFieldValue(project.landingTitle, LANDING_DEFAULTS.landingTitle)
  );
  const [landingSubtitle, setLandingSubtitle] = useState(
    landingFieldValue(project.landingSubtitle, LANDING_DEFAULTS.landingSubtitle)
  );
  const [landingButton, setLandingButton] = useState(
    landingFieldValue(project.landingButton, LANDING_DEFAULTS.landingButton)
  );
  const [landingFooter, setLandingFooter] = useState(
    landingFieldValue(project.landingFooter, LANDING_DEFAULTS.landingFooter)
  );
  const [partnerLogo, setPartnerLogo] = useState(project.partnerLogo || null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: 10,
    color: theme.text,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 13,
    color: theme.textMuted,
    marginBottom: 6,
  };

  const panel = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: theme.radius,
    padding: 24,
    marginBottom: 16,
    boxShadow: theme.shadow,
  };

  const agentUrl = `https://elevenlabs.io/app/agents/agents/${encodeURIComponent(agentId || project.agentId)}`;
  const previewLogo = partnerLogo || '/60-DB.png';

  const resetColors = () => {
    setPrimaryColor(COLOR_DEFAULTS.primaryColor);
    setBackgroundColor(COLOR_DEFAULTS.backgroundColor);
    setButtonTextColor(COLOR_DEFAULTS.buttonTextColor);
    setMessage('');
  };

  const handleLogo = async (file) => {
    setError('');
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg)$/i.test(file.type)) {
      setError('Please upload a PNG or JPG image.');
      return;
    }
    if (file.size > 1_400_000) {
      setError('Image is too large. Please use a file under ~1.4MB.');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setPartnerLogo(dataUrl);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const updated = await updateProject(project.slug, {
        name,
        agentId,
        defaultTag,
        requireMicGate,
        primaryColor,
        backgroundColor,
        buttonTextColor,
        landingTitle,
        landingSubtitle,
        landingButton,
        landingFooter,
        partnerLogo,
      });
      onUpdated(updated);
      setMessage('Settings saved.');
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (deleteText.trim() !== project.slug) {
      setError(`Type "${project.slug}" exactly to confirm deletion.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await deleteProject(project.slug);
      onDeleted();
    } catch (err) {
      setError(err.message || 'Failed to delete project');
      setBusy(false);
    }
  };

  return (
    <div>
      <div
        style={{
          backgroundColor: 'rgba(234, 179, 8, 0.12)',
          border: '1px solid rgba(234, 179, 8, 0.45)',
          borderRadius: theme.radius,
          padding: 16,
          marginBottom: 16,
          color: theme.mode === 'dark' ? '#FDE68A' : '#854D0E',
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        <strong>Note:</strong> The ElevenLabs voice widget must be configured on the agent itself
        in ElevenLabs (prompt, first message, dynamic variables like{' '}
        <code>{'{{name}}'}</code> / <code>{'{{respondent_id}}'}</code>).
        Open this project&apos;s agent:{' '}
        <a
          href={agentUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', fontWeight: 700 }}
        >
          {agentUrl}
        </a>
      </div>

      <form onSubmit={handleSave} style={panel}>
        <h2 style={{ margin: '0 0 16px', fontSize: 16, color: theme.text }}>Project settings</h2>

        <div style={{ display: 'grid', gap: 14, maxWidth: 520 }}>
          <div>
            <label style={labelStyle}>Project name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>ElevenLabs agent ID</label>
            <input
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              required
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Default tag</label>
            <input value={defaultTag} onChange={(e) => setDefaultTag(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <h3 style={{ margin: '28px 0 8px', fontSize: 15, color: theme.text }}>
          Microphone check screen
        </h3>
        <p style={{ margin: '0 0 16px', color: theme.textMuted, fontSize: 13, lineHeight: 1.5 }}>
          When on, respondents see a branded start screen and must allow the microphone before the
          voice agent opens. When off, the agent loads immediately.
        </p>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '14px 16px',
            borderRadius: 12,
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.surfaceMuted,
            marginBottom: requireMicGate ? 20 : 8,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
              Require mic check before start
            </div>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 3 }}>
              {requireMicGate ? 'On - show landing screen first' : 'Off - open widget directly'}
            </div>
          </div>
          <Toggle
            checked={requireMicGate}
            onChange={setRequireMicGate}
            accent={theme.accent}
          />
        </div>

        {requireMicGate && (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={{ ...labelStyle, marginBottom: 8 }}>
                Load default copy into the fields (you can edit after)
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(LANGUAGE_PRESETS).map(([code, preset]) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => {
                      setLandingTitle(preset.landingTitle);
                      setLandingSubtitle(preset.landingSubtitle);
                      setLandingButton(preset.landingButton);
                      setLandingFooter(preset.landingFooter);
                      setMessage('');
                    }}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 999,
                      border: `1px solid ${theme.border}`,
                      background: theme.surfaceMuted,
                      color: theme.text,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gap: 14, maxWidth: 520, marginBottom: 18 }}>
              <CopyField
                label="Landing title"
                value={landingTitle}
                onChange={setLandingTitle}
                placeholder={LANDING_DEFAULTS.landingTitle}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
                theme={theme}
              />
              <CopyField
                label="Landing subtitle"
                value={landingSubtitle}
                onChange={setLandingSubtitle}
                placeholder={LANDING_DEFAULTS.landingSubtitle}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
                theme={theme}
                multiline
              />
              <CopyField
                label="Start button"
                value={landingButton}
                onChange={setLandingButton}
                placeholder={LANDING_DEFAULTS.landingButton}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
                theme={theme}
              />
              <CopyField
                label="Footer tip"
                value={landingFooter}
                onChange={setLandingFooter}
                placeholder={LANDING_DEFAULTS.landingFooter}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
                theme={theme}
                multiline
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 12,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ fontSize: 13, color: theme.textMuted }}>Colors</div>
              <button
                type="button"
                onClick={resetColors}
                style={{
                  padding: '7px 12px',
                  borderRadius: 999,
                  border: `1px solid ${theme.border}`,
                  background: theme.surface,
                  color: theme.textMuted,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Reset colors to defaults
              </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 18 }}>
              <ColorField
                label="Primary / button color"
                value={primaryColor}
                onChange={setPrimaryColor}
                theme={theme}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
              />
              <ColorField
                label="Background color"
                value={backgroundColor}
                onChange={setBackgroundColor}
                theme={theme}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
              />
              <ColorField
                label="Button text color"
                value={buttonTextColor}
                onChange={setButtonTextColor}
                theme={theme}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>
                Logo (PNG or JPG) - replaces the default 60 Decibels logo
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <label
                  style={{
                    display: 'inline-flex',
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: `1px dashed ${theme.border}`,
                    background: theme.surfaceMuted,
                    color: theme.text,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {partnerLogo ? 'Replace logo' : 'Upload logo'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    style={{ display: 'none' }}
                    onChange={(e) => handleLogo(e.target.files[0])}
                  />
                </label>
                <img
                  src={previewLogo}
                  alt="Logo preview"
                  style={{ height: 48, objectFit: 'contain', maxWidth: 160 }}
                />
                {partnerLogo && (
                  <button
                    type="button"
                    onClick={() => setPartnerLogo(null)}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: theme.danger,
                      cursor: 'pointer',
                      fontSize: 13,
                      fontFamily: 'inherit',
                    }}
                  >
                    Use 60 Decibels logo
                  </button>
                )}
              </div>
            </div>

            <div
              style={{
                borderRadius: theme.radius,
                border: `1px solid ${theme.border}`,
                padding: 24,
                backgroundColor: backgroundColor,
                textAlign: 'center',
                marginBottom: 20,
              }}
            >
              <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 14 }}>Preview</div>
              <img
                src={previewLogo}
                alt="Survey logo"
                style={{
                  height: 72,
                  objectFit: 'contain',
                  maxWidth: '80%',
                  marginBottom: 20,
                }}
              />
              {landingTitle ? (
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 20,
                    color: '#1A1A1A',
                    marginBottom: 8,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {landingTitle}
                </div>
              ) : null}
              {landingSubtitle ? (
                <p
                  style={{
                    fontSize: 14,
                    color: 'rgba(26,26,26,0.65)',
                    lineHeight: 1.5,
                    margin: '0 0 18px',
                    maxWidth: 320,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                >
                  {landingSubtitle}
                </p>
              ) : (
                <div style={{ marginBottom: 18 }} />
              )}
              <button
                type="button"
                style={{
                  padding: '12px 18px',
                  border: 'none',
                  borderRadius: 10,
                  backgroundColor: primaryColor,
                  color: buttonTextColor,
                  fontWeight: 600,
                  fontSize: 14,
                  minWidth: 120,
                  minHeight: 42,
                }}
              >
                {landingButton || '\u00a0'}
              </button>
              {landingFooter ? (
                <p
                  style={{
                    marginTop: 16,
                    fontSize: 12,
                    color: 'rgba(26,26,26,0.4)',
                    lineHeight: 1.4,
                    maxWidth: 320,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                >
                  {landingFooter}
                </p>
              ) : null}
            </div>
          </>
        )}

        {error && <p style={{ color: theme.danger, fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        {message && <p style={{ color: theme.success, fontSize: 13, margin: '0 0 12px' }}>{message}</p>}

        <button
          type="submit"
          disabled={busy}
          style={{
            padding: '12px 18px',
            borderRadius: 10,
            border: 'none',
            background: busy ? theme.surfaceMuted : theme.accent,
            color: busy ? theme.textFaint : '#fff',
            fontWeight: 600,
            fontSize: 14,
            cursor: busy ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {busy ? 'Saving...' : 'Save settings'}
        </button>
      </form>

      <div style={{ ...panel, borderColor: 'rgba(220,38,38,0.35)' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 16, color: theme.danger }}>Delete project</h2>
        <p style={{ margin: '0 0 16px', color: theme.textMuted, fontSize: 14, lineHeight: 1.5 }}>
          This permanently deletes the project and <strong>all unique links</strong> stored for it.
          This cannot be undone.
        </p>

        {!confirmDelete ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            style={{
              padding: '11px 16px',
              borderRadius: 10,
              border: `1px solid rgba(220,38,38,0.45)`,
              background: 'rgba(220,38,38,0.08)',
              color: theme.danger,
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Delete this project...
          </button>
        ) : (
          <div>
            <p style={{ color: theme.text, fontSize: 14, margin: '0 0 10px' }}>
              Type <code>{project.slug}</code> to confirm:
            </p>
            <input
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              style={{ ...inputStyle, maxWidth: 320, marginBottom: 12 }}
              placeholder={project.slug}
            />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={busy || deleteText.trim() !== project.slug}
                onClick={handleDelete}
                style={{
                  padding: '11px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background:
                    busy || deleteText.trim() !== project.slug
                      ? theme.surfaceMuted
                      : theme.danger,
                  color:
                    busy || deleteText.trim() !== project.slug ? theme.textFaint : '#fff',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor:
                    busy || deleteText.trim() !== project.slug ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Delete forever
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                  setDeleteText('');
                }}
                style={{
                  padding: '11px 16px',
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                  background: 'transparent',
                  color: theme.textMuted,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, accent }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 52,
        height: 30,
        borderRadius: 999,
        border: 'none',
        padding: 3,
        backgroundColor: checked ? accent : 'rgba(120,120,120,0.35)',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      <span
        style={{
          display: 'block',
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          transform: checked ? 'translateX(22px)' : 'translateX(0)',
          transition: 'transform 0.2s ease',
        }}
      />
    </button>
  );
}

function ColorField({ label, value, onChange, theme, inputStyle, labelStyle }) {
  return (
    <div style={{ minWidth: 160 }}>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 42, height: 38, border: 'none', background: 'none', cursor: 'pointer' }}
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, width: 110, fontFamily: theme.mono, fontSize: 13 }}
        />
      </div>
    </div>
  );
}

function CopyField({
  label,
  value,
  onChange,
  placeholder,
  inputStyle,
  labelStyle,
  theme,
  multiline,
}) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <label style={{ ...labelStyle, marginBottom: 0 }}>{label}</label>
        <button
          type="button"
          onClick={() => onChange('')}
          style={{
            border: 'none',
            background: 'none',
            color: theme.textMuted,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            padding: 0,
          }}
        >
          Leave blank
        </button>
      </div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}
    </div>
  );
}

export default ProjectSettings;
